import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Building2, Mail, Phone, Plus, FolderKanban } from 'lucide-react';
import { useStore } from '../store/useStore';
import { formatDate, formatCurrency } from '../lib/utils';
import { ProjectBadge } from '../components/Badge';
import ProgressBar from '../components/ProgressBar';

export default function ClientDetail() {
  const { clientId } = useParams<{ clientId: string }>();
  const navigate = useNavigate();
  const clients = useStore((s) => s.clients);
  const projects = useStore((s) => s.projects);
  const users = useStore((s) => s.users);

  const client = clients.find((c) => c.id === clientId);
  const clientProjects = projects.filter((p) => p.clientId === clientId);

  if (!client) {
    return (
      <div className="p-8 text-center text-gray-400">
        Client not found. <button onClick={() => navigate('/clients')} className="text-brand-500">Go back</button>
      </div>
    );
  }

  const getUser = (id: string) => users.find((u) => u.id === id);

  return (
    <div className="p-8 max-w-5xl mx-auto">
      {/* Back */}
      <button
        onClick={() => navigate('/clients')}
        className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-gray-700 mb-6 transition-colors"
      >
        <ArrowLeft size={14} /> Clients
      </button>

      {/* Client header */}
      <div className="bg-white rounded-xl border border-gray-100 p-6 mb-6">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-xl bg-brand-50 flex items-center justify-center">
              <Building2 size={24} className="text-brand-500" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">{client.companyName}</h1>
              <p className="text-gray-500 text-sm mt-0.5">{client.contactPerson}</p>
            </div>
          </div>
          <div className="text-right text-sm text-gray-400">
            <div>Client since {formatDate(client.createdAt)}</div>
            <div className="mt-1 font-medium text-gray-600">{clientProjects.length} projects</div>
          </div>
        </div>

        <div className="mt-5 flex flex-wrap gap-4">
          {client.email && (
            <a href={`mailto:${client.email}`} className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-brand-500 transition-colors">
              <Mail size={14} className="text-gray-400" /> {client.email}
            </a>
          )}
          {client.phone && (
            <a href={`tel:${client.phone}`} className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-brand-500 transition-colors">
              <Phone size={14} className="text-gray-400" /> {client.phone}
            </a>
          )}
        </div>

        {client.notes && (
          <div className="mt-4 px-4 py-3 bg-gray-50 rounded-lg">
            <p className="text-xs font-medium text-gray-400 mb-1">Notes</p>
            <p className="text-sm text-gray-700">{client.notes}</p>
          </div>
        )}
      </div>

      {/* Projects */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-semibold text-gray-900">Projects</h2>
        <button
          onClick={() => navigate('/projects', { state: { newProjectClientId: client.id } })}
          className="flex items-center gap-1.5 text-sm text-brand-500 hover:text-brand-700 font-medium"
        >
          <Plus size={14} /> New Project
        </button>
      </div>

      <div className="space-y-3">
        {clientProjects.length === 0 && (
          <div className="bg-white rounded-xl border border-gray-100 p-8 text-center">
            <FolderKanban size={32} className="text-gray-200 mx-auto mb-3" />
            <p className="text-gray-400 text-sm">No projects yet for this client.</p>
          </div>
        )}
        {clientProjects.map((project) => {
          const pm = getUser(project.projectManagerId);
          const sales = getUser(project.salesOwnerId);
          return (
            <button
              key={project.id}
              onClick={() => navigate(`/projects/${project.id}`)}
              className="w-full bg-white rounded-xl border border-gray-100 p-5 text-left hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-gray-900">{project.name}</span>
                    <ProjectBadge status={project.status} />
                  </div>
                  <div className="text-xs text-gray-400 mb-3">
                    Sales: {sales?.name} · PM: {pm?.name} · {formatDate(project.startDate)} → {formatDate(project.endDate)}
                  </div>
                  <ProgressBar value={project.overallCompletion} size="md" showLabel />
                </div>
                <div className="text-right shrink-0">
                  <div className="font-bold text-gray-900">{formatCurrency(project.value, project.currency)}</div>
                  <div className="text-xs text-gray-400 mt-0.5">{project.overallCompletion}% complete</div>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
